/**
 * Estado dos accordions top-level da sidebar.
 *
 * Cada categoria (Legado, Financeiro, Comercial, etc.) tem estado independente
 * de aberto/fechado, persistido em localStorage por `moduloId`. Por padrao,
 * apenas a categoria correspondente a rota atual abre na primeira visita.
 *
 * Forma do estado em localStorage:
 *   { "legado": true, "comercial": false, "producao": true, ... }
 *
 * API:
 *   - estado: dict de moduloId -> bool (aberto)
 *   - toggle(moduloId): inverte o estado
 *   - setAberto(moduloId, bool): forca abrir/fechar
 *   - garantirAberto(moduloId): so abre, nao fecha (uso: ao navegar para uma
 *     rota, garantimos que a categoria dela apareca; se usuario fechou
 *     manualmente outra, respeita).
 */
import { useCallback, useEffect, useState } from "react";

// Bugfix 2026-08-04: mesmo problema do useSidebarPreference — o nome estava
// com o prefixo legado, que `migrarPrefixosLegados()` renomeia no boot, entao
// os menus abertos nunca sobreviviam a um F5. Ver comentario la para o detalhe.
const STORAGE_KEY = "pharuerp:sidebar:top-level-aberto";

function lerEstadoInicial(moduloAtivoId, todosModuloIds) {
  if (typeof window === "undefined") return {};
  let salvo = {};
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (raw) salvo = JSON.parse(raw) || {};
  } catch {
    salvo = {};
  }
  // Primeira visita ou usuario nunca abriu nada: abre a categoria da rota
  // atual + categoria "legado" (entry point natural da cliente-piloto hoje).
  // Subsequentes: respeita o salvo.
  if (Object.keys(salvo).length === 0) {
    const inicial = {};
    if (moduloAtivoId) inicial[moduloAtivoId] = true;
    return inicial;
  }
  // Garante que o modulo da rota atual abra (mesmo se nao tava no salvo)
  if (moduloAtivoId && salvo[moduloAtivoId] === undefined) {
    salvo[moduloAtivoId] = true;
  }
  return salvo;
}

export function useTopLevelAccordion(moduloAtivoId, todosModuloIds = []) {
  const [estado, setEstado] = useState(() =>
    lerEstadoInicial(moduloAtivoId, todosModuloIds)
  );

  // Persiste em localStorage a cada mudanca
  useEffect(() => {
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(estado));
    } catch {
      /* storage indisponivel — ignora */
    }
  }, [estado]);

  // Garante que ao mudar de rota, a nova categoria abra automaticamente.
  // Nao fecha as outras — respeita o que o usuario abriu manualmente.
  useEffect(() => {
    if (!moduloAtivoId) return;
    setEstado((prev) => {
      if (prev[moduloAtivoId]) return prev;
      return { ...prev, [moduloAtivoId]: true };
    });
  }, [moduloAtivoId]);

  const toggle = useCallback((moduloId) => {
    setEstado((prev) => ({ ...prev, [moduloId]: !prev[moduloId] }));
  }, []);

  const setAberto = useCallback((moduloId, aberto) => {
    setEstado((prev) => ({ ...prev, [moduloId]: Boolean(aberto) }));
  }, []);

  const garantirAberto = useCallback((moduloId) => {
    setEstado((prev) => (prev[moduloId] ? prev : { ...prev, [moduloId]: true }));
  }, []);

  return { estado, toggle, setAberto, garantirAberto };
}
